/**
 * Reusable chemical selector with autocomplete from dosing_chemicals table.
 * Allows selecting from predefined chemicals or typing a custom name.
 */
import { useMemo } from 'react';
import {
  Combobox,
  InputBase,
  useCombobox,
  Loader,
} from '@mantine/core';
import { useDosingChemicals } from '@/hooks/useDosingEvents';

interface ChemicalSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export function ChemicalSelect({
  value,
  onChange,
  label = 'Chemical',
  placeholder = 'Select or type chemical name',
  required,
  error,
}: ChemicalSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const { data: chemicalsData, isLoading } = useDosingChemicals();

  const chemicalOptions = useMemo(() => {
    if (!chemicalsData?.chemicals) return [];
    return chemicalsData.chemicals.map((c) => ({
      value: c.name,
      label: c.name,
    }));
  }, [chemicalsData]);

  const filteredOptions = useMemo(() => {
    const search = value.toLowerCase().trim();
    if (!search) return chemicalOptions;
    return chemicalOptions.filter((opt) =>
      opt.label.toLowerCase().includes(search)
    );
  }, [chemicalOptions, value]);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={isLoading ? <Loader size={16} /> : null}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <Combobox.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>
              {value.trim()
                ? `"${value}" will be used as entered`
                : 'No chemicals found'}
            </Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
